import React, { useCallback, useRef } from 'react'
import {
  useMyPresence,
  isLiveblocksEligible,
  type Presence,
} from '@/lib/liveblocks'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { useAuth } from '@/hooks/useAuth'
import { useIsRoomActive } from './RoomShell'
import { PresenceCursors } from './PresenceLayer'

interface PresenceCanvasOverlayProps {
  children: React.ReactNode
  /**
   * Passed through to the wrapper div. The parent is expected to be
   * `position: relative` already; if not, add 'relative' here.
   */
  className?: string
  /**
   * Throttle publishes in ms. Liveblocks client already throttles to
   * 50ms globally, so a small local additional throttle further cuts
   * the number of setPresence calls when a user is wildly flailing
   * the mouse. Defaults to 30 — tight enough to feel live, slack
   * enough that React re-renders don't pile up.
   */
  moveThrottleMs?: number
}

/**
 * Wraps a canvas region (React Flow editor, sequencer pad grid) in
 * a pointer tracker that publishes the local user's cursor into
 * Liveblocks presence, and paints remote peers' cursors on top.
 *
 * Pass-through when the caller isn't eligible for multiplayer or
 * isn't inside a `<RoomProvider>` — same gate as `MaybePresenceDock`
 * so consumers can drop this wrapper unconditionally.
 *
 * Coordinates are **container-relative pixels** (rect.left/top
 * subtracted), not flow-graph coords. That means on a React Flow
 * canvas that's been panned, a peer's cursor shows where their
 * pointer is relative to their own viewport — not anchored to a
 * specific device. Acceptable v0; a follow-up can translate
 * through `useReactFlow().screenToFlowPosition` for true
 * device-anchored cursors.
 */
export const PresenceCanvasOverlay: React.FC<PresenceCanvasOverlayProps> = ({
  children,
  className,
  moveThrottleMs = 30,
}) => {
  const { features } = useEffectiveFeatures()
  const { user } = useAuth()
  // `useIsRoomActive` is the authoritative check — only a true value
  // means a `<RoomProvider>` is genuinely mounted above us and the
  // Liveblocks hooks in `PresenceCanvasOverlayInner` are safe to
  // call. The eligibility + user checks are kept as a fast-path
  // short-circuit so we don't pay a context read when the user isn't
  // on a multiplayer-capable tier at all.
  const roomActive = useIsRoomActive()
  if (!roomActive || !user || !user.id || !isLiveblocksEligible(features)) {
    // No-op wrapper — still renders children inside a plain div so
    // the caller's layout (flex children etc.) is unaffected.
    return <div className={className}>{children}</div>
  }
  return (
    <PresenceCanvasOverlayInner
      className={className}
      moveThrottleMs={moveThrottleMs}
    >
      {children}
    </PresenceCanvasOverlayInner>
  )
}

/**
 * Inner component — safely calls `useMyPresence`, which requires
 * an active `<RoomProvider>`. We only mount this when the outer
 * gate confirms eligibility, matching the conditions in
 * `RoomShell`.
 */
const PresenceCanvasOverlayInner: React.FC<PresenceCanvasOverlayProps> = ({
  children,
  className,
  moveThrottleMs = 30,
}) => {
  const [, setPresence] = useMyPresence()
  // Throttle publishes to avoid spamming Liveblocks on every pointer
  // tick. A pending coord is held; a fresh timer flushes it.
  const pending = useRef<{ x: number; y: number } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flush = useCallback(() => {
    timer.current = null
    if (pending.current) {
      setPresence({ cursor: pending.current } as Partial<Presence>)
      pending.current = null
    }
  }, [setPresence])

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      pending.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
      if (timer.current == null) {
        timer.current = setTimeout(flush, moveThrottleMs)
      }
    },
    [flush, moveThrottleMs],
  )

  const onPointerLeave = useCallback(() => {
    pending.current = null
    if (timer.current != null) {
      clearTimeout(timer.current)
      timer.current = null
    }
    setPresence({ cursor: null } as Partial<Presence>)
  }, [setPresence])

  return (
    <div
      className={className}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      {children}
      <PresenceCursors />
    </div>
  )
}
